import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Plus, PencilSimple, TrashSimple, MagnifyingGlass,
  Buildings, UsersThree, Receipt, CurrencyCircleDollar, CaretRight,
  X, ClipboardText, LinkSimple, TreeStructure, ArrowLeft,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

const emptyItem = { no: "", name: "", unit: "m2", quantity: 0, unit_price: 0, vat_rate: 20, wbs_node_id: "" };
const emptyForm = {
  subcontractor_id: "",
  site_id: "",
  title: "",
  scope: "",
  sign_date: new Date().toISOString().slice(0, 10),
  start_date: "",
  end_date: "",
  formality: "resmi",
  contract_type: "birim_fiyat",   // Faz 34.24 — Sözleşme Tipi
  currency: "TRY",
  vat_rate: 20,
  advance_amount: 0,
  retention_rate: 5,
  provisional_retention_rate: 0,
  kdv_tevkifat: "4/10",
  damga_rate: 0.948,
  stopaj_rate: 0,
  items: [{ ...emptyItem }],
  status: "taslak",
  notes: "",
};

// Sözleşme tipi seçenekleri (Faz 34.24)
const CONTRACT_TYPE_OPTIONS = [
  { v: "anahtar_teslim", label: "Anahtar Teslim Götürü Bedel",
    desc: "Sabit toplam tutar; miktar riski yükleniciye ait" },
  { v: "birim_fiyat", label: "Birim Fiyat Esaslı Sözleşme",
    desc: "Poz cetveline göre birim × metraj" },
  { v: "adam_gun", label: "Adam/Gün Esaslı Sözleşme",
    desc: "İşçilik günlüğü × yevmiye" },
  { v: "karma", label: "Karma Sözleşme",
    desc: "Farklı kalemler farklı yöntemlere göre" },
];

const STATUS_META = {
  taslak: { label: "Taslak", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  aktif: { label: "Aktif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  askida: { label: "Askıda", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  kapali: { label: "Kapalı", cls: "bg-slate-100 text-slate-500 border-slate-300" },
  iptal: { label: "İptal", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const TEVKIFAT_OPTIONS = ["yok", "2/10", "3/10", "4/10", "5/10", "7/10", "9/10"];

const fmt = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "-"
    : Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// -------- WBS Leaf Picker --------
function WbsLeafPicker({ leaves, value, onChange, testId, disabled, suggestedIds }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = value ? leaves.find((l) => l.node_id === value) : null;
  const suggestedSet = useMemo(() => new Set(suggestedIds || []), [suggestedIds]);
  const filtered = useMemo(() => {
    const nq = (q || "").toLowerCase().trim();
    const base = nq
      ? leaves.filter((l) =>
          (l.name || "").toLowerCase().includes(nq) ||
          (l.code_path || "").toLowerCase().includes(nq) ||
          (l.category || "").toLowerCase().includes(nq))
      : leaves;
    // Sorumluluk matrisi ile bağlı olan yapraklar önce, diğerleri sonra
    const suggested = base.filter((l) => suggestedSet.has(l.node_id));
    const others = base.filter((l) => !suggestedSet.has(l.node_id));
    return { suggested: suggested.slice(0, 40), others: others.slice(0, 40) };
  }, [leaves, q, suggestedSet]);
  const renderRow = (l, isSuggested) => {
    const isSel = l.node_id === value;
    return (
      <button key={l.node_id} type="button"
              onClick={() => { onChange(l.node_id); setOpen(false); setQ(""); }}
              data-testid={`${testId}-opt-${l.node_id}`}
              className={`w-full text-left px-2 py-1.5 border-b border-slate-100 hover:bg-slate-50 transition ${
                isSel ? "bg-[#F7FBE9] border-l-2 border-l-[#95C11F]" :
                (isSuggested ? "border-l-2 border-l-amber-400" : "")
              }`}>
        <div className="flex items-center gap-1.5">
          {isSuggested && (
            <span className="inline-flex px-1 py-0.5 rounded-sm bg-amber-500 text-white text-[8px] uppercase tracking-widest font-bold flex-shrink-0"
                  title="Sorumluluk matrisinde bu ekibe bağlı">
              Öneri
            </span>
          )}
          <span className="inline-flex px-1 py-0.5 rounded-sm bg-[#95C11F]/15 text-[8px] uppercase tracking-widest font-bold text-[#5a7a10] flex-shrink-0">
            {l.category || "—"}
          </span>
          <span className="text-xs font-bold text-slate-900 truncate">{l.name}</span>
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5 truncate" title={l.code_path}>
          {l.code_path}
        </div>
        {l.total_metraj > 0 && (
          <div className="text-[10px] text-[#1B4776] font-mono font-bold mt-0.5">
            Metraj: {Number(l.total_metraj).toLocaleString("tr-TR")} {l.unit || ""}
          </div>
        )}
      </button>
    );
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
                data-testid={testId}
                disabled={disabled}
                className={`w-full inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest border transition ${
                  selected
                    ? "bg-[#F7FBE9] border-[#95C11F] text-[#556B10] hover:brightness-95"
                    : "bg-slate-50 border-slate-300 text-slate-500 hover:border-[#1B4776] hover:text-[#1B4776]"
                } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
          {selected ? (
            <>
              <TreeStructure size={10} weight="bold" />
              <span className="truncate normal-case tracking-normal font-semibold text-[10px]" title={selected.code_path}>
                {selected.name}
              </span>
            </>
          ) : (
            <>
              <LinkSimple size={10} weight="bold" />
              <span>WBS Bağla</span>
              {suggestedSet.size > 0 && (
                <span className="ml-1 inline-flex items-center px-1 rounded-sm bg-amber-500 text-white text-[8px] font-bold" title="Sorumluluk matrisinden öneri var">
                  {suggestedSet.size}
                </span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 rounded-none sm:rounded-sm" align="start">
        <div className="p-2 border-b border-slate-200 bg-slate-50">
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Poz adı, kategori veya yol ara..."
                 className="h-8 text-xs rounded-none sm:rounded-sm"
                 autoFocus />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {leaves.length === 0 ? (
            <div className="p-4 text-center text-[11px] text-rose-600">
              Bu şantiye için WBS pozu tanımlı değil.
              <div className="text-[10px] text-slate-500 mt-1">
                Önce WBS Kırınımları'na yaprak (Poz) düğümleri ekleyin.
              </div>
            </div>
          ) : (filtered.suggested.length === 0 && filtered.others.length === 0) ? (
            <div className="p-4 text-center text-[11px] text-slate-500">
              Aramaya uygun poz bulunamadı.
            </div>
          ) : (
            <>
              {filtered.suggested.length > 0 && (
                <>
                  <div className="px-2 py-1 bg-amber-50 border-b border-amber-200 text-[9px] uppercase tracking-widest font-bold text-amber-800">
                    ⭐ Sorumluluk Matrisi Önerileri
                  </div>
                  {filtered.suggested.map((l) => renderRow(l, true))}
                </>
              )}
              {filtered.others.length > 0 && (
                <>
                  {filtered.suggested.length > 0 && (
                    <div className="px-2 py-1 bg-slate-100 border-b border-slate-200 text-[9px] uppercase tracking-widest font-bold text-slate-600">
                      Diğer Pozlar
                    </div>
                  )}
                  {filtered.others.map((l) => renderRow(l, false))}
                </>
              )}
            </>
          )}
        </div>
        {selected && (
          <div className="p-2 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">
              Bağlı: {selected.name}
            </span>
            <button type="button"
                    onClick={() => { onChange(""); setOpen(false); }}
                    data-testid={`${testId}-clear`}
                    className="text-[10px] uppercase tracking-widest font-bold text-rose-600 hover:bg-rose-50 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1">
              <X size={10} weight="bold" /> Bağı Kaldır
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function SubContracts() {
  const nav = useNavigate();
  const { sites } = useSites();
  const { siteId } = useParams();
  const currentSite = useMemo(
    () => (sites || []).find((s) => s.id === siteId),
    [sites, siteId],
  );
  const isSiteScoped = !!siteId;

  const [rows, setRows] = useState([]);
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const confirm = useConfirm();

  const load = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      // Şantiye bazlı görüntülemedeyken URL'deki siteId zorunlu filtre
      if (isSiteScoped) params.site_id = siteId;
      else if (siteFilter !== "all") params.site_id = siteFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await axios.get(`${API}/subcontracts`, { params });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadSubs = async () => {
    try {
      const { data } = await axios.get(`${API}/subcontractors`);
      setSubs(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [q, siteFilter, statusFilter, siteId]); // eslint-disable-line
  useEffect(() => { loadSubs(); }, []);

  // WBS yaprakları — sözleşmenin şantiyesine göre yüklenir (Faz 28 — Hakediş Otomasyonu)
  const [wbsLeaves, setWbsLeaves] = useState([]);
  useEffect(() => {
    const sid = form.site_id;
    if (!sid) { setWbsLeaves([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/sites/${sid}/wbs-progress/matrix`);
        if (!cancelled) setWbsLeaves(data.leaves || []);
      } catch { if (!cancelled) setWbsLeaves([]); }
    })();
    return () => { cancelled = true; };
  }, [form.site_id]);

  // Sorumluluk matrisi — bu taşeronun ekibine bağlı WBS yaprakları öneri olarak sunulur
  const [teamSuggestedWbsIds, setTeamSuggestedWbsIds] = useState([]);
  useEffect(() => {
    const sid = form.site_id;
    const subId = form.subcontractor_id;
    if (!sid || !subId) { setTeamSuggestedWbsIds([]); return; }
    const sub = subs.find((s) => s.id === subId);
    const teamId = sub?.team_id || "";
    if (!teamId) { setTeamSuggestedWbsIds([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/responsibility-rules`, {
          params: { site_id: sid },
        });
        if (cancelled) return;
        const ids = new Set();
        (data || []).forEach((r) => {
          if (r.team_id === teamId) {
            (r.wbs_scope_ids || []).forEach((wid) => ids.add(wid));
          }
        });
        setTeamSuggestedWbsIds(Array.from(ids));
      } catch { if (!cancelled) setTeamSuggestedWbsIds([]); }
    })();
    return () => { cancelled = true; };
  }, [form.site_id, form.subcontractor_id, subs]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (idx, k, v) => setForm((f) => ({
    ...f,
    items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)),
  }));
  // WBS yaprağı bağlanınca imalat tanımı (name) ve varsa birim otomatik dolar.
  const linkWbs = (idx, nodeId) => {
    const leaf = nodeId ? wbsLeaves.find((l) => l.node_id === nodeId) : null;
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => {
        if (i !== idx) return it;
        const patch = { wbs_node_id: nodeId };
        if (leaf) {
          patch.name = leaf.name || it.name;
          if (leaf.unit && !it.unit) patch.unit = leaf.unit;
        }
        return { ...it, ...patch };
      }),
    }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const rmItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, site_id: isSiteScoped ? siteId : "" });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      subcontractor_id: r.subcontractor_id || "",
      site_id: r.site_id || "",
      title: r.title || "",
      scope: r.scope || "",
      sign_date: r.sign_date || "",
      start_date: r.start_date || "",
      end_date: r.end_date || "",
      formality: r.formality || "resmi",
      contract_type: r.contract_type || "birim_fiyat",
      currency: r.currency || "TRY",
      vat_rate: r.vat_rate ?? 20,
      advance_amount: r.advance_amount ?? 0,
      retention_rate: r.retention_rate ?? 5,
      provisional_retention_rate: r.provisional_retention_rate ?? 0,
      kdv_tevkifat: r.kdv_tevkifat || "4/10",
      damga_rate: r.damga_rate ?? 0.948,
      stopaj_rate: r.stopaj_rate ?? 0,
      items: (r.items || []).length ? r.items.map((it) => ({ ...emptyItem, ...it })) : [{ ...emptyItem }],
      status: r.status || "taslak",
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.subcontractor_id) return toast.error("Alt yüklenici / Taşeron seçmelisiniz");
      if (!form.site_id) return toast.error("Şantiye seçmelisiniz");
      if (!form.title.trim()) return toast.error("İş tanımı zorunludur");
      // Clean items — sadece adı olanları gönder
      const items = form.items.filter((it) => (it.name || "").trim());
      if (items.length === 0) return toast.error("En az bir iş kalemi eklemelisiniz");
      // Gayri resmi'de vergisel kesintiler sıfırlanır — backend zaten uygulamıyor
      // ama payload'da net olsun.
      const isInformal = (form.formality || "resmi") === "gayri_resmi";
      const payload = {
        ...form,
        items,
        vat_rate: isInformal ? 0 : (Number(form.vat_rate) || 0),
        kdv_tevkifat: isInformal ? "yok" : (form.kdv_tevkifat || "yok"),
        damga_rate: isInformal ? 0 : (Number(form.damga_rate) || 0),
        stopaj_rate: isInformal ? 0 : (Number(form.stopaj_rate) || 0),
      };
      if (editing) {
        await axios.put(`${API}/subcontracts/${editing.id}`, payload);
        toast.success("Sözleşme güncellendi");
      } else {
        await axios.post(`${API}/subcontracts`, payload);
        toast.success("Sözleşme oluşturuldu");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (r) => {
    const ok = await confirm({
      title: "Sözleşme Sil",
      description: `${r.code} - ${r.title} silinsin mi?`,
      confirmText: "Sil", variant: "destructive",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/subcontracts/${r.id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Dialog içi canlı toplam
  const dialogTotal = useMemo(() => {
    const totalNoVat = form.items.reduce(
      (s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0
    );
    const isInformal = (form.formality || "resmi") === "gayri_resmi";
    return {
      no_vat: totalNoVat,
      with_vat: isInformal
        ? totalNoVat
        : totalNoVat * (1 + (Number(form.vat_rate) || 20) / 100),
    };
  }, [form.items, form.vat_rate, form.formality]);

  const stats = useMemo(() => ({
    total: rows.length,
    aktif: rows.filter((r) => r.status === "aktif").length,
    taslak: rows.filter((r) => r.status === "taslak").length,
    toplamTutar: rows.reduce((s, r) => s + (r.total_no_vat || 0), 0),
    ilerlemeTutar: rows.reduce((s, r) => s + (r.progress_gross_total || 0), 0),
  }), [rows]);

  const { sort, toggleSort, sortedRows } = useSortableRows(rows, {
    defaultKey: "code", defaultDir: "asc",
    getters: {
      code: (r) => r.code || "",
      title: (r) => r.title || "",
      subcontractor_name: (r) => r.subcontractor_name || "",
      site_name: (r) => r.site_name || "",
      total_no_vat: (r) => Number(r.total_no_vat) || 0,
      progress_pct: (r) => Number(r.progress_pct) || 0,
      status: (r) => r.status || "",
    },
  });

  return (
    <Shell
      title={currentSite ? `${currentSite.name} — Sözleşmeler` : "Firma/Ekip Sözleşmeleri"}
      subtitle={isSiteScoped ? "Alt Yüklenici Yönetimi › Şantiye Bazlı" : "Alt Yüklenici Yönetimi"}
      hideSiteSelector={isSiteScoped}
    >
      {isSiteScoped && (
        <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex items-center gap-2 text-[11px] text-slate-600">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Şantiye:</span>
            {currentSite ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200">
                <span className="font-mono text-[10px] font-bold text-[#1B4776]">{currentSite.code}</span>
                <span className="text-slate-700">{currentSite.name}</span>
              </span>
            ) : <span className="text-slate-400">Bulunamadı</span>}
          </div>
          <Link to="/taseron/sozlesmeler">
            <Button variant="outline" data-testid="sc-back"
                    className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
              <ArrowLeft size={14} className="mr-1.5" /> Şantiye Listesi
            </Button>
          </Link>
        </div>
      )}
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Toplam Sözleşme</div>
          <div className="text-2xl font-bold text-[#1B4776] mt-1">{stats.total}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-emerald-50/50">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Aktif</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.aktif}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Taslak</div>
          <div className="text-2xl font-bold text-slate-700 mt-1">{stats.taslak}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-blue-50/50">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#1B4776] font-bold">Sözleşme Toplamı (KDV Hariç)</div>
          <div className="text-lg font-bold text-[#1B4776] mt-1 font-mono">₺{fmt(stats.toplamTutar)}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-[#F7FBE9]/60">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#556B10] font-bold">Yapılan İş (Hakediş Brüt)</div>
          <div className="text-lg font-bold text-[#556B10] mt-1 font-mono">₺{fmt(stats.ilerlemeTutar)}</div>
        </Card>
      </div>

      {/* Toolbar */}
      <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-3 flex-1 items-center">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="sc-search" placeholder="Kod, iş tanımı ara..." value={q}
                     onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-none sm:rounded-sm" />
            </div>
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className={`w-56 rounded-none sm:rounded-sm ${isSiteScoped ? "hidden" : ""}`} data-testid="sc-site-filter">
                <SelectValue placeholder="Şantiye" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Şantiyeler</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="font-mono text-xs text-[#1B4776] mr-2">{s.code}</span>{s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 rounded-none sm:rounded-sm" data-testid="sc-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={openNew}
                  className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm"
                  data-testid="sc-add-btn">
            <Plus size={14} weight="bold" className="mr-1.5" /> Yeni Sözleşme
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <SortableTh label="Kod" k="code" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-32" />
                <SortableTh label="İş Tanımı" k="title" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left" />
                <SortableTh label="Alt Yüklenici / Taşeron" k="subcontractor_name" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-44" />
                <SortableTh label="Şantiye" k="site_name" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-28" />
                <SortableTh label="Tutar (KDV Hariç)" k="total_no_vat" sort={sort} onSort={toggleSort} className="px-4 py-3 text-right w-36" align="right" />
                <SortableTh label="İlerleme" k="progress_pct" sort={sort} onSort={toggleSort} className="px-4 py-3 text-center w-32" align="center" />
                <SortableTh label="Durum" k="status" sort={sort} onSort={toggleSort} className="px-4 py-3 text-center w-24" align="center" />
                <th className="px-4 py-3 text-right w-28">İşlem</th>
              </tr>
            </thead>
            <tbody data-testid="sc-table-body">
              {sortedRows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                  <FileText size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">Henüz sözleşme yok — "Yeni Sözleşme" ile başlayın.</p>
                </td></tr>
              )}
              {sortedRows.map((r, i) => {
                const isT = r.subcontractor_kind === "taseron";
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`sc-row-${i}`}>
                    <td className="px-4 py-3 font-mono text-xs font-bold text-[#1B4776]">{r.code}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{r.title}</div>
                      {r.sign_date && (
                        <div className="text-[10px] text-slate-500 mt-0.5">İmza: {r.sign_date}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {isT ? (
                          <UsersThree size={12} weight="bold" className="text-[#556B10]" />
                        ) : (
                          <Buildings size={12} weight="bold" className="text-[#1B4776]" />
                        )}
                        <span className="text-xs font-semibold">{r.subcontractor_name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {r.site_code && (
                        <span className="inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-blue-50 text-[#1B4776] border border-blue-200"
                              title={r.site_name}>{r.site_code}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">
                      <div className="text-slate-800 font-bold">₺{fmt(r.total_no_vat)}</div>
                      {r.formality === "gayri_resmi" ? (
                        <div className="text-[10px] text-amber-700 font-bold">Gayri Resmi (KDV yok)</div>
                      ) : (
                        <div className="text-[10px] text-slate-400">KDV Dahil: ₺{fmt(r.total_with_vat)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#95C11F]"
                               style={{ width: `${Math.min(100, r.progress_pct || 0)}%` }} />
                        </div>
                        <span className="text-[10px] font-mono font-bold text-slate-700 w-10 text-right">
                          %{r.progress_pct || 0}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 text-center">
                        {r.progress_count || 0} hakediş
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${STATUS_META[r.status]?.cls || STATUS_META.taslak.cls}`}>
                        {STATUS_META[r.status]?.label || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => nav(`/taseron/hakedis?contract_id=${r.id}`)}
                                className="p-1 text-slate-400 hover:text-[#95C11F]"
                                title="Hakedişler" data-testid={`sc-progress-${i}`}>
                          <Receipt size={14} weight="bold" />
                        </button>
                        <button onClick={() => openEdit(r)} className="p-1 text-slate-400 hover:text-[#1B4776]"
                                title="Düzenle" data-testid={`sc-edit-${i}`}>
                          <PencilSimple size={14} weight="bold" />
                        </button>
                        <button onClick={() => remove(r)} className="p-1 text-slate-400 hover:text-rose-600"
                                title="Sil" data-testid={`sc-del-${i}`}>
                          <TrashSimple size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl rounded-none sm:rounded-sm max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={18} weight="duotone" className="text-[#95C11F]" />
              {editing ? `Sözleşme Düzenle: ${editing.code}` : "Yeni Sözleşme"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* ADIM 1 — Şantiye + Ekip Seçimi (2 kolon) */}
            <Card className="p-3 rounded-none sm:rounded-sm border-slate-200 bg-slate-50">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] mb-2 flex items-center gap-2">
                <span className="bg-[#1B4776] text-white px-1.5 py-0.5 text-[9px]">1</span>
                Şantiye ve Taşeron Seçimi
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Sol — Şantiye */}
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    Şantiye *
                  </label>
                  <Select value={form.site_id}
                          onValueChange={(v) => {
                            set("site_id", v);
                            // Şantiye değişirse taşeron seçimini sıfırla (o siteye atanmamış olabilir)
                            if (form.subcontractor_id) {
                              const stillOk = subs.find(
                                (s) => s.id === form.subcontractor_id
                                  && (s.site_ids || []).includes(v)
                              );
                              if (!stillOk) set("subcontractor_id", "");
                            }
                          }}
                          disabled={!!editing}>
                    <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="sc-site-select">
                      <SelectValue placeholder="Önce şantiye seçin —" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="font-mono text-xs text-[#1B4776] mr-2">{s.code}</span>{s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-slate-500 mt-1">
                    Şantiye seçildikten sonra sağda atanmış ekipler listelenecek.
                  </div>
                </div>

                {/* Sağ — Ekip / Taşeron listesi */}
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    Şantiyeye Atanmış Taşeron / Ekip *
                  </label>
                  <div className="border border-slate-300 bg-white max-h-48 overflow-y-auto"
                       data-testid="sc-team-list">
                    {!form.site_id && (
                      <div className="p-4 text-center text-[11px] text-slate-400 italic">
                        Önce sol taraftan bir şantiye seçin
                      </div>
                    )}
                    {form.site_id && (() => {
                      const assigned = subs.filter(
                        (s) => (s.site_ids || []).includes(form.site_id)
                      );
                      if (assigned.length === 0) {
                        return (
                          <div className="p-4 text-center text-[11px] text-rose-600">
                            Bu şantiyeye atanmış taşeron/ekip yok.
                            <div className="text-slate-500 mt-1 text-[10px]">
                              Alt Yüklenici sayfasından şantiye ataması yapabilirsiniz.
                            </div>
                          </div>
                        );
                      }
                      return assigned.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          disabled={!!editing}
                          onClick={() => set("subcontractor_id", s.id)}
                          className={`w-full text-left px-3 py-2 border-b border-slate-100 flex items-center gap-2 transition-colors ${
                            form.subcontractor_id === s.id
                              ? "bg-[#F7FBE9] border-l-4 border-l-[#95C11F]"
                              : "hover:bg-slate-50"
                          } ${editing ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                          data-testid={`sc-team-${s.id}`}
                        >
                          {s.kind === "taseron" ? (
                            <UsersThree size={14} weight="duotone" className="text-[#556B10] flex-shrink-0" />
                          ) : (
                            <Buildings size={14} weight="duotone" className="text-[#1B4776] flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold text-slate-800 truncate">{s.name}</div>
                            {s.specialties && s.specialties.length > 0 && (
                              <div className="text-[10px] text-slate-500 truncate">
                                {s.specialties.slice(0, 3).join(" · ")}
                              </div>
                            )}
                          </div>
                          <span className="text-[9px] font-bold uppercase text-slate-400 tracking-widest flex-shrink-0">
                            {s.kind === "taseron" ? "Taşeron" : "Alt Yük."}
                          </span>
                          {form.subcontractor_id === s.id && (
                            <CaretRight size={12} weight="bold" className="text-[#95C11F] flex-shrink-0" />
                          )}
                        </button>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </Card>

            {/* ADIM 2 — İş Tanımı + Tarihler + Kayıt Türü */}
            <Card className="p-3 rounded-none sm:rounded-sm border-slate-200">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] mb-2 flex items-center gap-2">
                <span className="bg-[#1B4776] text-white px-1.5 py-0.5 text-[9px]">2</span>
                Sözleşme Bilgileri
              </div>
              <div className="space-y-3">
                {/* Kayıt Türü Radio */}
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    Kayıt Türü *
                  </label>
                  <div className="flex gap-2 mt-1">
                    {[
                      { v: "resmi", label: "Resmi", desc: "Vergisel kesintiler uygulanır", cls: "border-[#1B4776]" },
                      { v: "gayri_resmi", label: "Gayri Resmi", desc: "Vergisel kesintiler yok", cls: "border-amber-500" },
                    ].map((opt) => (
                      <button key={opt.v} type="button"
                        onClick={() => set("formality", opt.v)}
                        className={`flex-1 border-2 p-2 text-left transition-colors ${
                          form.formality === opt.v
                            ? `${opt.cls} bg-slate-50`
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`sc-formality-${opt.v}`}>
                        <div className="text-xs font-bold uppercase">{opt.label}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">İş Tanımı *</label>
                  <Input value={form.title} onChange={(e) => set("title", e.target.value)}
                         placeholder="Örn: Blok A Kaba İnşaat" className="rounded-none sm:rounded-sm"
                         data-testid="sc-title" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Sözleşme Tarihi</label>
                    <Input type="date" value={form.sign_date} onChange={(e) => set("sign_date", e.target.value)}
                           className="rounded-none sm:rounded-sm" data-testid="sc-sign-date" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">İşin Başlangıç Tarihi</label>
                    <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)}
                           className="rounded-none sm:rounded-sm" data-testid="sc-start-date" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">İşin Bitiş Tarihi</label>
                    <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)}
                           className="rounded-none sm:rounded-sm" data-testid="sc-end-date" />
                  </div>
                </div>
                {/* Sözleşme Tipi — Tarihlerin hemen altında (Faz 34.24) */}
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    Sözleşme Tipi *
                  </label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {CONTRACT_TYPE_OPTIONS.map((opt) => {
                      const active = form.contract_type === opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => set("contract_type", opt.v)}
                          data-testid={`sc-contract-type-${opt.v}`}
                          className={`text-left p-2.5 border transition-all ${
                            active
                              ? "border-[#1B4776] bg-[#1B4776]/5 ring-1 ring-[#1B4776]"
                              : "border-slate-200 hover:border-slate-400 bg-white"
                          }`}
                        >
                          <div className={`text-xs font-bold ${active ? "text-[#1B4776]" : "text-slate-700"}`}>
                            {opt.label}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {opt.desc}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card>

            {/* ADIM 3 — İmalat Miktarları (Ana Kalem + Yaklaşık Metrajlar) */}
            <Card className="p-3 rounded-none sm:rounded-sm border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] flex items-center gap-2">
                  <span className="bg-[#1B4776] text-white px-1.5 py-0.5 text-[9px]">3</span>
                  İmalat Miktarları — Ana Kalemler ve Yaklaşık Metrajlar
                </div>
                <Button type="button" size="sm" onClick={addItem}
                        className="bg-[#95C11F] hover:bg-[#7ea01a] text-white h-7 text-xs rounded-none sm:rounded-sm"
                        data-testid="sc-add-item">
                  <Plus size={12} weight="bold" className="mr-1" /> Ana Kalem Ekle
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-widest text-slate-500 bg-slate-50">
                      <th className="px-2 py-1.5 text-left w-24">Poz No</th>
                      <th className="px-2 py-1.5 text-left">Ana Kalem (İmalat Tanımı) *</th>
                      <th className="px-2 py-1.5 text-left w-20">Birim</th>
                      <th className="px-2 py-1.5 text-right w-28">Yaklaşık Metraj</th>
                      <th className="px-2 py-1.5 text-right w-32">Birim Fiyat ₺</th>
                      <th className="px-2 py-1.5 text-right w-32">Tutar ₺</th>
                      <th className="px-2 py-1.5 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const total = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                      return (
                        <tr key={i} className="border-b border-slate-100" data-testid={`sc-item-${i}`}>
                          <td className="p-1">
                            <Input value={it.no || ""}
                                   onChange={(e) => setItem(i, "no", e.target.value)}
                                   className="h-8 rounded-none sm:rounded-sm text-xs font-mono"
                                   placeholder="Örn: 21.001"
                                   data-testid={`sc-item-no-${i}`} />
                          </td>
                          <td className="p-1">
                            <Input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)}
                                   className="h-8 rounded-none sm:rounded-sm text-xs"
                                   placeholder="Örn: Duvar Örülmesi, Sıva İşi..."
                                   data-testid={`sc-item-name-${i}`} />
                            <div className="mt-1">
                              <WbsLeafPicker
                                leaves={wbsLeaves}
                                value={it.wbs_node_id || ""}
                                onChange={(v) => linkWbs(i, v)}
                                testId={`sc-item-wbs-${i}`}
                                disabled={!form.site_id}
                                suggestedIds={teamSuggestedWbsIds.filter((wid) =>
                                  // Bu satır için mevcut önerisi ise dahil et,
                                  // ama başka satırlar tarafından kullanılıyorsa çıkar
                                  wid === it.wbs_node_id ||
                                  !form.items.some((oit, oi) =>
                                    oi !== i && oit.wbs_node_id === wid
                                  )
                                )}
                              />
                            </div>
                          </td>
                          <td className="p-1">
                            <Input value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)}
                                   className="h-8 rounded-none sm:rounded-sm text-xs" placeholder="m2" />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="0.01" value={it.quantity}
                                   onChange={(e) => setItem(i, "quantity", parseFloat(e.target.value) || 0)}
                                   placeholder="yaklaşık"
                                   className="h-8 rounded-none sm:rounded-sm text-xs font-mono text-right" />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="0.01" value={it.unit_price}
                                   onChange={(e) => setItem(i, "unit_price", parseFloat(e.target.value) || 0)}
                                   className="h-8 rounded-none sm:rounded-sm text-xs font-mono text-right" />
                          </td>
                          <td className="p-1 text-right font-mono font-bold text-slate-800">
                            ₺{fmt(total)}
                          </td>
                          <td className="p-1 text-center">
                            {form.items.length > 1 && (
                              <button type="button" onClick={() => rmItem(i)}
                                      className="text-slate-400 hover:text-rose-600" data-testid={`sc-item-rm-${i}`}>
                                <X size={12} weight="bold" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {form.formality === "gayri_resmi" ? (
                      <tr className="bg-slate-50 border-t-2 border-[#1B4776]">
                        <td colSpan={5} className="px-2 py-2 text-right font-bold text-[10px] uppercase tracking-widest text-slate-600">
                          Sözleşme Toplamı
                          <span className="ml-2 text-[9px] text-amber-700 normal-case tracking-normal">
                            (KDV uygulanmaz — Gayri Resmi)
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right font-mono font-bold text-[#1B4776]">
                          ₺{fmt(dialogTotal.no_vat)}
                        </td>
                        <td></td>
                      </tr>
                    ) : (
                      <>
                        <tr className="bg-slate-50 border-t-2 border-[#1B4776]">
                          <td colSpan={5} className="px-2 py-2 text-right font-bold text-[10px] uppercase tracking-widest text-slate-600">
                            Sözleşme Toplamı (KDV Hariç)
                          </td>
                          <td className="px-2 py-2 text-right font-mono font-bold text-[#1B4776]">
                            ₺{fmt(dialogTotal.no_vat)}
                          </td>
                          <td></td>
                        </tr>
                        <tr className="bg-slate-100">
                          <td colSpan={5} className="px-2 py-1 text-right text-[10px] uppercase tracking-widest text-slate-500">
                            KDV Dahil Toplam
                          </td>
                          <td className="px-2 py-1 text-right font-mono text-slate-700">
                            ₺{fmt(dialogTotal.with_vat)}
                          </td>
                          <td></td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                </table>
              </div>
              <div className="text-[10px] text-slate-500 mt-2 italic">
                Metrajlar yaklaşıktır — hakediş aşamasında gerçekleşen miktarlar üzerinden düzeltme yapılır.
              </div>
            </Card>

            {/* ADIM 4 — Finansal Parametreler (kesinti oranları) */}
            <details className="border border-slate-200 rounded-none sm:rounded-sm" open>
              <summary className="p-3 bg-slate-50 cursor-pointer text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] flex items-center gap-2">
                <span className="bg-[#1B4776] text-white px-1.5 py-0.5 text-[9px]">4</span>
                Finansal Parametreler & Kesinti Oranları
                {form.formality === "gayri_resmi" && (
                  <span className="text-[9px] text-amber-700 normal-case tracking-normal ml-2 font-bold">
                    (Gayri Resmi — vergisel alanlar inaktif)
                  </span>
                )}
              </summary>
              <div className="p-3 grid grid-cols-3 gap-3">
                {(() => {
                  const isInformal = form.formality === "gayri_resmi";
                  const disabledCls = isInformal
                    ? "opacity-40 cursor-not-allowed bg-slate-100"
                    : "";
                  return (
                    <>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">KDV Oranı %</label>
                        <Input type="number" step="0.01" value={form.vat_rate}
                               onChange={(e) => set("vat_rate", parseFloat(e.target.value) || 0)}
                               disabled={isInformal}
                               className={`rounded-none sm:rounded-sm font-mono ${disabledCls}`}
                               data-testid="sc-vat-rate" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                          KDV Tevkifatı
                        </label>
                        <Select value={form.kdv_tevkifat}
                                onValueChange={(v) => set("kdv_tevkifat", v)}
                                disabled={isInformal}>
                          <SelectTrigger className={`rounded-none sm:rounded-sm ${disabledCls}`}
                                         data-testid="sc-tevkifat">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TEVKIFAT_OPTIONS.map((o) => (
                              <SelectItem key={o} value={o}>{o === "yok" ? "Yok" : o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Damga Vergisi %</label>
                        <Input type="number" step="0.001" value={form.damga_rate}
                               onChange={(e) => set("damga_rate", parseFloat(e.target.value) || 0)}
                               disabled={isInformal}
                               className={`rounded-none sm:rounded-sm font-mono ${disabledCls}`}
                               data-testid="sc-damga" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Stopaj %</label>
                        <Input type="number" step="0.1" value={form.stopaj_rate}
                               onChange={(e) => set("stopaj_rate", parseFloat(e.target.value) || 0)}
                               placeholder="Şahıs: 20, Kurum: 0"
                               disabled={isInformal}
                               className={`rounded-none sm:rounded-sm font-mono ${disabledCls}`}
                               data-testid="sc-stopaj" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kesin Teminat %</label>
                        <Input type="number" step="0.1" value={form.retention_rate}
                               onChange={(e) => set("retention_rate", parseFloat(e.target.value) || 0)}
                               className="rounded-none sm:rounded-sm font-mono"
                               data-testid="sc-kesin-teminat" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Geçici Teminat %</label>
                        <Input type="number" step="0.1" value={form.provisional_retention_rate}
                               onChange={(e) => set("provisional_retention_rate", parseFloat(e.target.value) || 0)}
                               className="rounded-none sm:rounded-sm font-mono"
                               data-testid="sc-gecici-teminat" />
                      </div>
                      <div className="col-span-3">
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                          Avans Tutarı ₺
                        </label>
                        <Input type="number" step="1" value={form.advance_amount}
                               onChange={(e) => set("advance_amount", parseFloat(e.target.value) || 0)}
                               className="rounded-none sm:rounded-sm font-mono"
                               data-testid="sc-advance" />
                      </div>
                    </>
                  );
                })()}
              </div>
              {form.formality === "gayri_resmi" && (
                <div className="px-3 pb-3 text-[10px] text-amber-800 bg-amber-50 border-t border-amber-200 py-2">
                  ⚠ <b>Gayri Resmi Kayıt:</b> KDV, KDV Tevkifatı, Damga Vergisi ve Stopaj alanları inaktif.
                  Sadece Kesin Teminat, Geçici Teminat ve Avans kesintileri hakedişte uygulanır.
                </div>
              )}
            </details>

            {/* Durum + Kapsam + Notlar */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Durum</label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="sc-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Para Birimi</label>
                <Input value={form.currency} onChange={(e) => set("currency", e.target.value)}
                       className="rounded-none sm:rounded-sm font-mono" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kapsam / Açıklama</label>
              <Textarea rows={2} value={form.scope} onChange={(e) => set("scope", e.target.value)}
                        className="rounded-none sm:rounded-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Notlar</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                        className="rounded-none sm:rounded-sm" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={save}
                    className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm"
                    data-testid="sc-save">
              {editing ? "Güncelle" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
